
import React from 'react';
import { format } from 'date-fns';
import { useRSVP } from '../../context/RSVPContext';
import { Button } from '@/components/ui/button';
import { RSVPButton } from '@/components/ui/rsvp-button';
import { CalendarIcon, Clock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { TimeOfDay } from '../../types';

export const DateTimeSelectionStep = () => {
  const { 
    eventData, 
    selectedDates, 
    setSelectedDates,
    selectedTimes,
    setSelectedTimes,
    rsvpResponse,
    setRsvpResponse,
    goToNextStep,
    goToPreviousStep
  } = useRSVP();

  const availableDates = eventData?.preferredDates || [];
  
  const availableTimes: TimeOfDay[] = (eventData?.preferredTimes || [])
    .filter((time): time is TimeOfDay => 
      time === 'morning' || time === 'afternoon' || time === 'evening'
    );

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return format(date, 'EEE, MMM d');
  };

  const toggleDate = (date: string) => {
    if (selectedDates.includes(date)) {
      setSelectedDates(selectedDates.filter(d => d !== date));
    } else {
      if (rsvpResponse !== 'yes') {
        setRsvpResponse('yes');
      }
      setSelectedDates([...selectedDates, date]);
    }
  };

  const toggleTime = (time: TimeOfDay) => {
    if (selectedTimes.includes(time)) {
      setSelectedTimes(selectedTimes.filter(t => t !== time));
    } else {
      setSelectedTimes([...selectedTimes, time]);
    }
  };

  const handleRSVPNo = () => {
    setRsvpResponse('no');
    setSelectedDates([]);
    setSelectedTimes([]);
    goToNextStep();
  };

  const handleContinue = () => {
    if (selectedDates.length > 0 && selectedTimes.length > 0) {
      goToNextStep();
    }
  };

  const canProceed = selectedDates.length > 0 && selectedTimes.length > 0;

  const handleBackToVerification = () => {
    goToPreviousStep();
  };

  return (
    <div className="flex flex-col h-full justify-between">
      <div className="flex-1 flex flex-col overflow-auto pb-24">  {/* Added pb-24 and overflow-auto */}
        <div className="h-[15vh] min-h-[80px] max-h-[120px]"></div>
        
        <div className="px-4 md:px-6">
          <div className="max-w-md mx-auto w-full">
            <h2 className="text-2xl font-semibold mb-6 text-center">Select Your Availability</h2>

            <div className="space-y-6">
              <div className="space-y-3">
                <div className="flex items-center mb-2">
                  <CalendarIcon className="mr-2 h-4 w-4 text-muted-foreground" />
                  <h3 className="text-sm font-medium text-muted-foreground">Available Dates</h3>
                </div>
                
                <div className="space-y-2">
                  {availableDates.map((date) => (
                    <button
                      key={date}
                      type="button"
                      className={cn(
                        "w-full text-left px-5 py-4 rounded-full flex items-center",
                        "transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
                        selectedDates.includes(date) 
                          ? "bg-black text-white" 
                          : "bg-muted hover:bg-muted/80 text-foreground"
                      )}
                      onClick={() => toggleDate(date)}
                    >
                      <CalendarIcon className="mr-3 h-4 w-4 flex-shrink-0" />
                      <span>{formatDate(date)}</span>
                    </button>
                  ))}
                </div>
              </div>

              {selectedDates.length > 0 && availableTimes.length > 0 && (
                <div className="space-y-3">
                  <div className="flex items-center mb-2">
                    <Clock className="mr-2 h-4 w-4 text-muted-foreground" />
                    <h3 className="text-sm font-medium text-muted-foreground">Preferred Times</h3>
                  </div>
                  
                  <div className="space-y-2">
                    {availableTimes.map((time) => (
                      <button
                        key={time}
                        type="button"
                        className={cn(
                          "w-full text-left px-5 py-4 rounded-full flex items-center",
                          "transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
                          selectedTimes.includes(time) 
                            ? "bg-black text-white" 
                            : "bg-muted hover:bg-muted/80 text-foreground"
                        )}
                        onClick={() => toggleTime(time)}
                      >
                        <Clock className="mr-3 h-4 w-4 flex-shrink-0" />
                        <span>{time.charAt(0).toUpperCase() + time.slice(1)}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-4">
                <button
                  type="button"
                  className="w-full text-center px-5 py-4 rounded-full bg-muted text-muted-foreground hover:bg-muted/80 transition-colors"
                  onClick={handleRSVPNo}
                >
                  Sorry, I can't make it
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {(selectedDates.length > 0 || rsvpResponse === 'no') && (
        <div className="fixed bottom-0 left-0 right-0 p-4 pb-12 bg-transparent">
          <div className="max-w-2xl mx-auto px-4 flex flex-col items-center">
            <RSVPButton 
              onClick={handleContinue}
              className="mx-auto block"
            >
              Next
            </RSVPButton>
            
            <Button 
              variant="link" 
              className="mt-2 text-sm text-muted-foreground"
              onClick={handleBackToVerification}
            >
              Change phone number
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
